import { supabase } from '../lib/supabase';
import { Family, FamilyPayment, FamilyPaymentStatus, Participant } from '../types/models';

export const getFamilies = async (): Promise<Family[]> => {
  const { data, error } = await supabase
    .from('families')
    .select(`
      *,
      department:departments(department_id, name, name_tamil),
      members:students!students_family_id_fkey(
        student_id,
        first_name,
        last_name,
        first_name_tamil,
        last_name_tamil,
        date_of_birth,
        gender,
        reference_number,
        payment_status,
        department_id,
        house_id
      )
    `)
    .order('family_name');

  if (error) throw new Error(error.message);
  return (data || []).map((f: any) => ({
    ...f,
    member_count: f.members?.length ?? 0,
  }));
};

export const getFamilyById = async (familyId: string): Promise<Family | null> => {
  const { data, error } = await supabase
    .from('families')
    .select(`
      *,
      department:departments(department_id, name, name_tamil),
      members:students!students_family_id_fkey(
        student_id,
        first_name,
        last_name,
        first_name_tamil,
        last_name_tamil,
        date_of_birth,
        gender,
        reference_number,
        payment_status,
        department_id,
        house_id
      )
    `)
    .eq('family_id', familyId)
    .maybeSingle();

  if (error) throw new Error(error.message);
  return data;
};

export const createFamily = async (input: {
  family_name: string;
  father_name?: string | null;
  mother_name?: string | null;
  guardian_name?: string | null;
  guardian_relationship?: string | null;
  contact_email?: string | null;
  contact_phone?: string | null;
  address?: string | null;
  notes?: string | null;
  department_id?: string | null;
}): Promise<Family> => {
  const { data, error } = await supabase
    .from('families')
    .insert([input])
    .select()
    .single();
  if (error) throw new Error(error.message);
  return data;
};

export const updateFamily = async (
  familyId: string,
  patch: Partial<Omit<Family, 'family_id' | 'created_at' | 'updated_at' | 'department' | 'members' | 'member_count'>>
): Promise<Family> => {
  const { data, error } = await supabase
    .from('families')
    .update(patch)
    .eq('family_id', familyId)
    .select()
    .single();
  if (error) throw new Error(error.message);
  return data;
};

export const deleteFamily = async (familyId: string): Promise<void> => {
  // Safety: students with this family_id will be set NULL via FK.
  const { error } = await supabase
    .from('families')
    .delete()
    .eq('family_id', familyId);
  if (error) throw new Error(error.message);
};

export const assignStudentToFamily = async (
  studentId: string,
  familyId: string | null
): Promise<void> => {
  const { error } = await supabase
    .from('students')
    .update({ family_id: familyId })
    .eq('student_id', studentId);
  if (error) throw new Error(error.message);
};

// Move a set of students into a new family (split). Returns the new family.
export const splitFamily = async (
  sourceFamily: Family,
  studentIds: string[],
  newFamilyName?: string
): Promise<Family> => {
  if (studentIds.length === 0) {
    throw new Error('Select at least one member to split off');
  }

  const newFamily = await createFamily({
    family_name: newFamilyName || sourceFamily.family_name,
    department_id: sourceFamily.department_id ?? null,
    contact_email: sourceFamily.contact_email ?? null,
    contact_phone: sourceFamily.contact_phone ?? null,
    address: sourceFamily.address ?? null,
  });

  const { error } = await supabase
    .from('students')
    .update({ family_id: newFamily.family_id })
    .in('student_id', studentIds);
  if (error) throw new Error(error.message);

  return newFamily;
};

// Move all members from source into target, then delete source.
export const mergeFamilies = async (sourceId: string, targetId: string): Promise<void> => {
  if (sourceId === targetId) {
    throw new Error('Cannot merge a family with itself');
  }

  const { error: moveError } = await supabase
    .from('students')
    .update({ family_id: targetId })
    .eq('family_id', sourceId);
  if (moveError) throw new Error(moveError.message);

  const { error: deleteError } = await supabase
    .from('families')
    .delete()
    .eq('family_id', sourceId);
  if (deleteError) throw new Error(deleteError.message);
};

export const getFamilyPayments = async (familyId: string): Promise<FamilyPayment[]> => {
  const { data, error } = await supabase
    .from('family_payments')
    .select('*')
    .eq('family_id', familyId)
    .order('competition_year', { ascending: false });
  if (error) throw new Error(error.message);
  return data || [];
};

export const getFamilyPaymentsForYear = async (year: number): Promise<FamilyPayment[]> => {
  const { data, error } = await supabase
    .from('family_payments')
    .select('*')
    .eq('competition_year', year);
  if (error) throw new Error(error.message);
  return data || [];
};

export const upsertFamilyPayment = async (input: {
  family_id: string;
  competition_year: number;
  status: FamilyPaymentStatus;
  amount?: number | null;
  paid_at?: string | null;
  method?: string | null;
  notes?: string | null;
}): Promise<FamilyPayment> => {
  const { data, error } = await supabase
    .from('family_payments')
    .upsert([input], { onConflict: 'family_id,competition_year' })
    .select()
    .single();
  if (error) throw new Error(error.message);
  return data;
};
